import { Link } from "react-router-dom";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import { useLang } from "../i18n/LanguageContext";

function RecognitionTable({ rows, testid, th1, th2, promoLabel, listinoLabel }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[460px] text-left border border-pine/10" data-testid={testid}>
        <thead>
          <tr className="bg-pine text-cream">
            <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em]">{th1}</th>
            <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em]">{th2}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.price} className="bg-surface border-t border-pine/10">
              <td className="px-6 py-4">
                <span className="font-serif text-2xl text-ink">{r.price}</span>
                <span className={`ms-3 inline-block align-middle text-[0.6rem] font-bold uppercase tracking-[0.2em] px-2.5 py-1 ${
                  r.promo ? "bg-gold/20 text-ink border border-gold/50" : "bg-pine/10 text-pine border border-pine/20"
                }`}>
                  {r.promo ? promoLabel : listinoLabel}
                </span>
              </td>
              <td className="px-6 py-4 font-serif text-2xl text-pine">{r.rec}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Partner() {
  const { t } = useLang();

  return (
    <div data-testid="partner-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24">
        <MaskedLine delay={0.1}><span className="eyebrow">{t("pp_eyebrow")}</span></MaskedLine>
        <h1 className="mt-8 max-w-4xl text-4xl sm:text-5xl lg:text-6xl leading-[1.08]" data-testid="partner-page-title">
          <MaskedLine delay={0.25}>
            <>{t("pp_title_pre")}<em className="italic text-gold">{t("pp_title_em")}</em>{t("pp_title_post")}</>
          </MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-2xl text-lg text-ink-muted leading-relaxed">{t("pp_text")}</p>
        </Reveal>
        <Reveal delay={0.5}>
          <div className="mt-10 card-surface p-6 sm:p-8 border-s-2 !border-s-gold max-w-2xl" data-testid="membership-note">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-pine">{t("pp_mem_label")}</p>
            <p className="mt-3 text-lg text-ink-muted leading-relaxed">
              {t("pp_mem_a")} <span className="text-ink font-semibold">{t("pp_mem_b")}</span>{t("pp_mem_c")} <span className="text-ink font-semibold">{t("pp_mem_d")}</span>.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-surface border-y border-pine/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 min-w-0">
            <Reveal><Eyebrow>{t("pp_f1_eyebrow")}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 text-3xl sm:text-4xl leading-tight" data-testid="fase1-title">
                {t("pp_f1_pre")} <em className="italic text-gold">{t("pp_f1_em")}</em>{t("pp_f1_post")}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8 min-w-0">
            <Reveal delay={0.15}>
              <RecognitionTable
                testid="fase1-table"
                th1={t("pp_th1")} th2={t("pp_th2")}
                promoLabel={t("pp_promo")} listinoLabel={t("pp_listino")}
                rows={[
                  { price: "€ 159", rec: "€ 100", promo: true },
                  { price: "€ 399", rec: "€ 200", promo: false },
                ]}
              />
              <p className="mt-5 text-base text-ink-muted leading-relaxed">{t("pp_f1_note")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4 min-w-0">
          <Reveal><Eyebrow>{t("pp_f2_eyebrow")}</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 text-3xl sm:text-4xl leading-tight" data-testid="fase2-title">
              {t("pp_f2_pre")} <em className="italic text-gold">{t("pp_f2_em")}</em>.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg text-ink-muted leading-relaxed">
              {t("pp_req_pre")} <span className="text-ink font-semibold">€ 100.000</span>.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-8 min-w-0">
          <Reveal delay={0.15}>
            <RecognitionTable
              testid="fase2-table"
              th1={t("pp_th1")} th2={t("pp_th2")}
              promoLabel={t("pp_promo")} listinoLabel={t("pp_listino")}
              rows={[
                { price: "€ 159", rec: "€ 159", promo: true },
                { price: "€ 399", rec: "€ 249", promo: false },
              ]}
            />
            <ul className="mt-8 space-y-3 text-base text-ink-muted" data-testid="fase2-conditions">
              {[t("pp_c1"), t("pp_c2"), t("pp_c3")].map((c) => (
                <li key={c} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rotate-45 bg-gold shrink-0" aria-hidden="true" />{c}</li>
              ))}
            </ul>
            <p className="mt-6 text-base text-ink-muted">{t("pp_fixed")}</p>
            <p className="mt-6 text-xs leading-relaxed text-ink-muted border border-pine/15 bg-surface px-5 py-4" data-testid="partner-note">
              {t("pp_note")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-pine/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl leading-tight max-w-2xl" data-testid="partner-closing-title">
              {t("pp_close_pre")} <em className="italic text-gold">{t("pp_close_em")}</em>{t("pp_close_post")}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-10">
            <Link to="/contatti" className="btn-primary" data-testid="partner-page-cta">{t("pp_cta")}</Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
